import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { EntityState } from '@ngrx/entity';
import { createFeatureSelector, createSelector, Store } from '@ngrx/store';
import { addDays, startOfDay } from 'date-fns';
import { NgxPermissionsService } from 'ngx-permissions';
import { Observable, OperatorFunction, combineLatest, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, tap, withLatestFrom } from 'rxjs/operators';
import { Encounter } from '@mona/models';
import { EncountersState } from '@mona/pdms/data-access-encounters';
import { DateIntervalService, DateRangeInterval, EntriesInterval } from '@mona/shared/date';
import { get, notEmpty } from '@mona/shared/utils';
import { selectRouteParam } from '@mona/store';

const selectEncountersState = createFeatureSelector<EncountersState>('encountersData');
const selectCurrentEncounterState = createFeatureSelector('currentEncounterData');

export const selectSelectedEncounter = createSelector(
    selectEncountersState,
    selectRouteParam('encounterId'),
    (encounters: EntityState<any>, encounterId) => encounters?.entities?.[encounterId],
);

/** Encounter View Selected Date */
export const selectencounterViewSelectedDate = createSelector(
    selectCurrentEncounterState,
    (state: { selectedDate: Date }) => {
        return state?.selectedDate;
    },
);

/** Encounter View Selected Date */
export const selectEncounterReviewMode = createSelector(
    selectCurrentEncounterState,
    (state: { isReviewMode: boolean }) => {
        return state?.isReviewMode;
    },
);

/** Encounter archived status */
export const selectEncounterArchivedStatus = createSelector(
    selectCurrentEncounterState,
    (state: { isArchived: boolean }) => {
        return state?.isArchived;
    },
);

export const selectSelectedEncounterId = createSelector(selectEncountersState, s => s?.selectedId);

/**
 * Emits when the user is no longer on an encounter screen.
 *
 * Used as the cancel/abort source by ~25 encounter-data load effects (task list,
 * daily goals, balance, medications, procedures, vital signs, lab values,
 * ventilation, anamnesis, allergies, diagnoses, vaccinations, audit log,
 * care reports, notifications, etc.) — typically as
 * `takeUntil(isEmptyEncounterId(this.store))` on the API observable, or as the
 * cancel branch of a `race(...)` wrapping the load.
 *
 * MUST be sourced from the route param, not `EncountersState.selectedId`.
 * The two are decoupled in the lifecycle: `selectedId` is set by
 * `EncounterComponent.subscribeOnEncounterIdChange` *after* the encounter
 * component subscribes to the route observable and dispatches
 * `EncountersActions.selectEncounter`, while `withCurrentEncounterId()` /
 * `currentEncounterId()` and `isSameEncounterFilter` all consume
 * `selectRouteParam('encounterId')`.
 *
 * Sourcing this notifier from `selectedId` re-introduces a transient false
 * positive on every login / encounter switch: when the resolver / container's
 * `ngAfterViewInit` dispatches a load on a fresh encounter route, `selectedId`
 * is still null for a few ticks, the cancel branch wins synchronously, and
 * the load is aborted before the API request even goes out — leaving every
 * panel empty (and, for effects whose reducer flips an `isLoading` flag,
 * stuck on a spinner). This was the visible-on-web shape of MD-9750: even
 * after the spinner cosmetic was patched, "log in → task list → log out → log
 * in → all panels empty forever; navigate away and back to recover" stayed
 * broken until this notifier was switched to the route-based source.
 * @param store
 */
export function isEmptyEncounterId(store: Store): Observable<any> {
    return store.select(selectRouteParam('encounterId')).pipe(
        distinctUntilChanged(),
        filter(encounterId => !encounterId),
    );
}

/**
 * `isReviewMode` as factory based on Angular `inject` mechabism
 */
export function isReviewMode(): Observable<boolean> {
    const store = inject(Store);
    return store.select(selectEncounterReviewMode).pipe(distinctUntilChanged());
}

/**
 * is encounter archived
 */
export function isArchived(): Observable<boolean> {
    const store = inject(Store);
    return store.select(selectSelectedEncounter).pipe(
        distinctUntilChanged(),
        map(enc => enc?.isArchived),
    );
}
/**
 * Shows if user is able to edit patient info
 */
export function isAbleToEdit(): Observable<boolean> {
    const permissionsService = inject(NgxPermissionsService);
    return combineLatest([isReviewMode(), isArchived()]).pipe(
        map(
            ([isReview, isArchived]) =>
                !isReview || (!!permissionsService.getPermission('review_mode_edit') && !isArchived),
        ),
    );
}

/**
 * Select current encounter property
 * @param prop
 */
export function selectCurrentEncounterProp<P extends Path<Encounter>>(prop: P) {
    return createSelector(selectSelectedEncounter, (encounter: Encounter) => get(encounter, prop));
}

/**
 * Get current encounter property as an observable
 * @param prop
 */
export function getCurrentEncounterProp<P extends Path<Encounter>>(prop: P): Observable<PathValue<Encounter, P>> {
    const store = inject(Store);
    return store.select(selectCurrentEncounterProp(prop));
}

/**
 * Encounter Start Date
 * @param asStartOfDay
 * @usageNotes uses {@link inject} to get the store, so use in constructor only
 */
export const getEncounterStartDate = (asStartOfDay = false) => {
    const store = inject(Store);
    return store.select(selectCurrentEncounterProp('startDate')).pipe(
        notEmpty(),
        map(date => (asStartOfDay ? startOfDay(date) : date)),
    );
};

/**
 * Encounter View Selected Date
 * used to filter all views which use day calendar
 * @usageNotes uses {@link inject} to get the store, so use in constructor only
 */
export const getEncounterViewSelectedDate = () => {
    const store = inject(Store);
    return store.select(selectencounterViewSelectedDate).pipe(distinctUntilChanged());
};

/**
 * Encounter View Date range
 * used to filter all views which use day calendar
 * @param daysInFuture
 * @usageNotes uses {@link inject} to get the store, so use in constructor only
 */
export const getEncounterViewDateRange = (daysInFuture = 1): Observable<DateRangeInterval> => {
    const store = inject(Store);
    const dateIntervalService = inject(DateIntervalService);
    return combineLatest([
        store.select(selectCurrentEncounterProp('startDate')),
        store.select(selectCurrentEncounterProp('endDate')),
        dateIntervalService.getTimeChangeTrigger(of(EntriesInterval.DAY_1)),
    ]).pipe(
        map(([startDate, endDate]) => ({ startDate, endDate })),
        filter(({ startDate }) => !!startDate),
        map(({ startDate, endDate }) => {
            let maxDate: Date;

            // If encounterhas an end date (discharged), limit max date to encounter end date
            if (endDate) {
                maxDate = endDate;
            } else {
                maxDate = addDays(new Date(), daysInFuture);
            }

            return {
                start: startDate,
                end: maxDate,
            };
        }),
    );
};

/**
 * 🤖 Operator to join Current Encounter Id to the source
 */
export function withCurrentEncounterId<T>(): OperatorFunction<T, [T, string]> {
    const store = inject(Store);
    return withLatestFrom(
        store.select(selectRouteParam('encounterId')).pipe(
            notEmpty(),
            // TODO: throw error if encounterId is empty
            /* mergeMap((encounterId: string | undefined) => isEmptyString(encounterId) ? throwError(new Error('Router parameter for `encounterId` not found')) : encounterId, ), */
        ),
    );
}

/**
 * @param store
 */
export function currentEncounterId<T>(store: Store): OperatorFunction<T, [T, string]> {
    return withLatestFrom(store.select(selectRouteParam('encounterId')));
}
